// ABOUTME: Floating XP gain indicators that animate upward and fade
// ABOUTME: Non-blocking overlay for immediate feedback on XP gains

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect } from 'react'
import { useReducedMotion } from '@/hooks/useReducedMotion'

interface XPGain {
  id: string
  amount: number
}

interface XPGainIndicatorProps {
  gains: XPGain[]
  onGainComplete: (id: string) => void
}

export function XPGainIndicator({ gains, onGainComplete }: XPGainIndicatorProps) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <div className="fixed top-20 right-4 z-40 pointer-events-none">
      <AnimatePresence>
        {gains.map((gain, index) => (
          <XPGainItem
            key={gain.id}
            gain={gain}
            index={index}
            onComplete={() => onGainComplete(gain.id)}
            reducedMotion={prefersReducedMotion}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

interface XPGainItemProps {
  gain: XPGain
  index: number
  onComplete: () => void
  reducedMotion: boolean
}

function XPGainItem({ gain, index, onComplete, reducedMotion }: XPGainItemProps) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 800)
    return () => clearTimeout(timer)
  }, [onComplete])

  const variants = reducedMotion
    ? {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, y: 0, scale: 0.8 },
        animate: {
          opacity: 1,
          y: -20,
          scale: 1,
          transition: { duration: 0.2 },
        },
        exit: {
          opacity: 0,
          y: -40,
          transition: { duration: 0.6 },
        },
      }

  return (
    <motion.div
      className="xp-gain mb-2"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ marginTop: index * 8 }}
    >
      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[var(--color-xp)]/20 text-[var(--color-xp)] font-semibold text-xs uppercase tracking-wider shadow-lg">
        +{gain.amount} XP
      </span>
    </motion.div>
  )
}
